import React, { FC, useEffect, useState } from 'react';
import FormField from '@components/form_field';
import styles from './styles.module.scss';
import Input from '@components/common/input';
import { useLocales } from '@hooks/helpers/useLocales';
import CanadaPostAutocomplete from '@components/common/canada_post_autocomplete';
import { useFormContext, useWatch } from 'react-hook-form';
import { Country } from 'country-state-city';
import { TAddress } from '@xeppt/xeppt-sdk/types';

interface IProps {
    name: string;
    disabled?: boolean;
    isFull?: boolean;
    isHelper?: boolean;
    isShowAutocomplete?: boolean;
    label?: string;
}

const FullAddress: FC<IProps> = ({
    name,
    disabled,
    isFull = true,
    isShowAutocomplete,
    isHelper,
    label
}) => {
    const { labelLocale } = useLocales();
    const [isAddressFiled, setIsAddressField] = useState(false);
    const [isRerender, setIsRerender] = useState(true);
    const { setValue, control } = useFormContext();

    const value = useWatch({ name, control });

    useEffect(() => {
        if (value?.address_1) {
            setIsAddressField(true);
        }
    }, [value]);

    return (
        <div className={styles.wrapper}>
            {isShowAutocomplete && (
                <FormField<TAddress>
                    name={name}
                    renderComponent={({ onChange, errors, ...props }) => (
                        <CanadaPostAutocomplete
                            {...props}
                            placeholder="Enter address"
                            onChange={(val) => {
                                onChange(val);
                                setValue(
                                    `${name}.country`,
                                    Country.getAllCountries().find(
                                        (item) => item.isoCode === val.country
                                    )?.name || val.country
                                );
                                setIsRerender(false);
                                setTimeout(() => {
                                    setIsRerender(true);
                                }, 0);
                            }}
                            helperText={
                                !!errors?.[name] ? 'Invalid address' : isFull ? undefined : ''
                            }
                            label={label}
                            full
                            isHelper={isHelper}
                            error={!!errors?.[name]}
                            disabled={disabled}
                        />
                    )}
                />
            )}
            {isFull && isAddressFiled && isRerender && (
                <div className={styles.address_wrapper}>
                    <FormField
                        name={`${name}.address_1`}
                        renderComponent={(props) => (
                            <Input
                                {...props}
                                full
                                label={labelLocale('address_1')}
                                readOnly={disabled}
                            />
                        )}
                    />
                    <FormField
                        name={`${name}.address_2`}
                        renderComponent={(props) => (
                            <Input
                                {...props}
                                full
                                label={labelLocale('address_2')}
                                readOnly={disabled}
                            />
                        )}
                    />
                    <div className={styles.row}>
                        <FormField
                            name={`${name}.country`}
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('country')}
                                    readOnly={disabled}
                                />
                            )}
                        />
                        <FormField
                            name={`${name}.city`}
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('city')}
                                    readOnly={disabled}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.row}>
                        <FormField
                            name={`${name}.region`}
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('region')}
                                    readOnly={disabled}
                                />
                            )}
                        />
                        <FormField
                            name={`${name}.zip_code`}
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('zip_code')}
                                    readOnly={disabled}
                                />
                            )}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FullAddress;
